import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useProduct } from '../hooks/useProducts';

const ProductDetail = () => {
  const { id } = useParams();
  const { product, loading, error } = useProduct(id);

  const additionalOptions = [
    { value: 'installation', label: 'Installation complète', description: 'Installation professionnelle par nos équipes certifiées', price: 'Sur devis' },
    { value: 'maintenance', label: 'Contrat maintenance annuel', description: 'Maintenance préventive et curative pendant 1 an', price: 'Sur devis' },
    { value: 'autonomy', label: 'Kit autonomie solaire', description: 'Panneaux solaires et batteries pour fonctionnement autonome', price: 'Sur devis' },
    { value: 'extended_warranty', label: 'Garantie étendue 5 ans', description: 'Extension de garantie constructeur à 5 ans', price: 'Sur devis' },
    { value: 'monitoring', label: 'Système monitoring IoT', description: 'Surveillance et contrôle à distance via application', price: 'Sur devis' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du produit...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Produit non trouvé</h2>
          <p className="text-gray-600 mb-4">Le produit demandé n'existe pas.</p>
          <Link
            to="/produits"
            data-testid="back-to-products"
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Voir tous les produits
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-20" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex mb-8" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-4">
            <li>
              <Link to="/" className="text-gray-400 hover:text-gray-500">Accueil</Link>
            </li>
            <li>
              <svg className="flex-shrink-0 h-5 w-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
              </svg>
            </li>
            <li>
              <Link to="/produits" className="text-gray-400 hover:text-gray-500">Produits</Link>
            </li>
            <li>
              <svg className="flex-shrink-0 h-5 w-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
              </svg>
            </li>
            <li>
              <span className="text-gray-700 font-medium">{product.name}</span>
            </li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-96 object-contain"
                data-testid="product-image"
              />
            </div>
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <span className="inline-block px-3 py-1 text-sm font-medium bg-blue-100 text-blue-800 rounded-full capitalize mb-4">
                {product.category}
              </span>
              {product.popular && (
                <span className="ml-2 inline-block px-3 py-1 text-sm font-medium bg-red-100 text-red-800 rounded-full">
                  Populaire
                </span>
              )}
              <h1 className="text-3xl font-bold text-gray-900 mt-2" data-testid="product-name">{product.name}</h1>
              <div className="flex items-center space-x-4 mt-4">
                <span className="text-3xl font-bold text-blue-600" data-testid="product-capacity">
                  {product.capacity_liters} L/jour
                </span>
                {product.available_for_purchase ? (
                  <span className="text-3xl font-bold text-green-600" data-testid="product-price">
                    {product.price.toLocaleString('fr-FR')}€
                  </span>
                ) : (
                  <span className="text-3xl font-bold text-green-600" data-testid="product-price">
                    Prix sur devis
                  </span>
                )}
              </div>
            </div>

            <div className="prose prose-gray">
              <p className="text-lg text-gray-700">{product.description}</p>
            </div>

            {/* Features */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Caractéristiques principales</h3>
              <ul className="grid grid-cols-1 gap-2">
                {product.features.map((feature) => (
                  <li key={feature} className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* Specifications */}
            {product.specifications && Object.keys(product.specifications).length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Spécifications techniques</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <dl className="grid grid-cols-1 gap-y-3">
                    {Object.entries(product.specifications).map(([key, value]) => (
                      <div key={key} className="flex justify-between items-center text-sm border-b border-gray-200 pb-2 last:border-b-0">
                        <dt className="font-medium text-gray-700 capitalize">
                          {key.replace(/_/g, ' ')} :
                        </dt>
                        <dd className="text-gray-900 font-medium text-right max-w-[60%]">{value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>
            )}

            {/* Additional Options */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Options supplémentaires disponibles</h4>
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="space-y-3">
                  {additionalOptions.map((option) => (
                    <div key={option.value} className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center mt-0.5">
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{option.label}</div>
                        <p className="text-xs text-gray-600 mt-1">{option.description}</p>
                        <p className="text-sm font-medium text-green-600 mt-1">{option.price}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Price Summary */}
            <div className="bg-green-50 rounded-lg p-6">
              <div className="text-center">
                {product.available_for_purchase ? (
                  <span className="text-2xl font-bold text-green-600">
                    {product.price.toLocaleString('fr-FR')}€
                  </span>
                ) : (
                  <div>
                    <span className="text-2xl font-bold text-green-600">Prix sur devis</span>
                    <p className="text-sm text-gray-600 mt-2">
                      Contactez-nous pour obtenir un devis personnalisé adapté à vos besoins spécifiques.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Maintenance and Support */}
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Maintenance et Support
              </h4>
              <div className="space-y-2">
                {product.id === 'cloud20' ? (
                  <>
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Contrat maintenance :</span> 250€/an
                    </p>
                    <p className="text-sm text-gray-600">
                      Remplacement des 5 filtres (PP, GAC, UF, BIO, ELE) selon volume produit
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Contrat maintenance :</span> 350€/an
                    </p>
                    <p className="text-sm text-gray-600">
                      Remplacement filtres multi-barrières + nettoyage circuits + contrôle UV-C
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-4">
              {product.available_for_purchase ? (
                <>
                  <Link
                    to={`/checkout?product=${product.id}`}
                    data-testid="buy-button"
                    className="w-full bg-green-600 hover:bg-green-700 text-white px-6 py-4 rounded-lg font-medium transition-colors text-center text-lg"
                  >
                    Commander - {product.price.toLocaleString('fr-FR')}€
                  </Link>
                  <Link
                    to={`/checkout?product=${product.id}&paiement=soguafi&duree=36+mois`}
                    data-testid="soguafi-button"
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white px-6 py-4 rounded-lg font-medium transition-colors text-center text-lg flex items-center justify-center"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Souscrire avec SOGUAFI
                  </Link>
                  <p className="text-xs text-gray-500 text-center -mt-2">
                    Un credit vous engage et doit etre rembourse. Verifiez vos capacites de remboursement avant de vous engager.
                  </p>
                </>
              ) : (
                <Link
                  to={`/devis?product=${product.id}`}
                  data-testid="quote-button"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-4 rounded-lg font-medium transition-colors text-center text-lg"
                >
                  Demander un devis gratuit
                </Link>
              )}
              <a
                href="tel:+590590197070"
                data-testid="call-button"
                className="w-full border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white px-6 py-4 rounded-lg font-medium transition-colors text-center text-lg flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                Appelez-nous : 0590 19 70 70
              </a>
            </div>

            {/* Contact Information */}
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-medium text-gray-900 mb-3">Questions sur ce produit ?</h4>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span className="font-medium">Téléphone : 0590 19 70 70</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="font-medium">Email : contact@ecoclima.energy</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
